/**
 * Yayın kaplamalarının yoklama döngüleri için hata sonrası geri çekilme.
 *
 * Sunucu saatlik sorgu kotasına takıldığında (503) ya da hız sınırına
 * çarptığında (429) kaplamalar tam hızda yoklamaya devam ederse kota dolu
 * kaldıkça istek yağmuru sürer ve site bir saate varan süre kapalı kalır.
 *
 * Sağlıklı yolda HİÇBİR ŞEY değişmez: zamanlayıcı aynı aralıkla çalışır,
 * `ready()` daima true döner. Bir yoklama başarısız olunca sonraki yoklama
 * taban aralık × 2^hata kadar ertelenir (en çok ~60 sn, biraz rastgele
 * sapmayla ki tüm kaplamalar aynı anda dönmesin). 429/503 yanıtında sayısal
 * `Retry-After` başlığı varsa en az o kadar beklenir (en çok 5 dk). İlk
 * başarılı yanıtta sayaç sıfırlanır.
 *
 * Kullanım (mevcut setInterval döngüsüne kapı olarak):
 *   const backoff = createPollBackoff(POLL_MS);
 *   const poll = async () => {
 *     if (!backoff.ready()) return;
 *     try { await ...; backoff.success(); } catch (err) { backoff.failure(err); }
 *   };
 */

export const BACKOFF_MAX_MS = 60 * 1000;
export const RETRY_AFTER_MAX_MS = 5 * 60 * 1000;

/** 429/503 yanıtındaki sayısal Retry-After başlığını ms olarak döndürür. */
export const readRetryAfterMs = (error, capMs = RETRY_AFTER_MAX_MS) => {
  const response = error?.response;
  const status = Number(response?.status);
  if (status !== 429 && status !== 503) return 0;
  const headers = response?.headers || {};
  const raw =
    typeof headers.get === "function"
      ? headers.get("retry-after")
      : headers["retry-after"] ?? headers["Retry-After"];
  const seconds = Number(raw);
  if (!Number.isFinite(seconds) || seconds <= 0) return 0;
  return Math.min(seconds * 1000, capMs);
};

/** `failures` ardışık hatadan sonra beklenecek süre (ms). */
export const backoffDelayMs = (
  baseMs,
  failures,
  error,
  { maxMs = BACKOFF_MAX_MS, retryAfterCapMs = RETRY_AFTER_MAX_MS, random = Math.random } = {}
) => {
  if (!failures) return 0;
  const exp = Math.min(maxMs, baseMs * 2 ** Math.min(failures, 20));
  // %0–25 arası sapma; tavanı aşmaz.
  const jittered = Math.min(maxMs, Math.round(exp * (1 + random() * 0.25)));
  return Math.max(jittered, readRetryAfterMs(error, retryAfterCapMs));
};

export function createPollBackoff(baseMs, options = {}) {
  const now = options.now || (() => Date.now());
  let failures = 0;
  let resumeAt = 0;

  return {
    /** Bu tikte yoklama yapılabilir mi? Sağlıklı yolda daima true. */
    ready: () => now() >= resumeAt,
    success: () => {
      failures = 0;
      resumeAt = 0;
    },
    /** Hata sonrası bekleme süresini kurar ve döndürür (ms). */
    failure: (error) => {
      failures += 1;
      const delay = backoffDelayMs(baseMs, failures, error, options);
      resumeAt = now() + delay;
      return delay;
    },
    get failures() {
      return failures;
    },
  };
}
